import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { Card, Table, Button, Spinner, Badge, OverlayTrigger, Tooltip, Dropdown, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTable, useSortBy, useFilters, useExpanded } from "react-table";
import { Star } from "react-feather";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSort, faSortUp, faSortDown, faRobot, faInfoCircle, faUser } from "@fortawesome/free-solid-svg-icons";
import NotyfContext from "../contexts/NotyfContext";
import { UserSettingsContext } from "../contexts/UserSettingsContext";
import authenticatedFetch from "./fetch";
import rawTaskCategories from "./taskCategories";
import rawTaskStatuses from "./taskStatuses";
import TaskStatusBadge from "./TaskStatusBadge";
import TaskListRow from "./TaskListRow";
import TaskAdditionalActionsModal from "./TaskAdditionalActionsModal";
import TaskAdditionalInstructionsModal from "./TaskAdditionalInstructionsModal";
import getAvailableCarrierSubmissionActionTypeDisplay from "./autoActionTypes";
import EditableExpires from "./EditableExpires";
import { getRelativeTimeDisplay } from "./relativeDate";
import { getStickyStyle, STAR_COLUMN_WIDTH, STAR_COLUMN_WIDTH_WITH_HANDLE } from "./stickyColumn";

// Shared task list: the cells, column set and table used by My Tasks, Team Tasks and the Tasks page.

const rawTaskStatusesWithShortLabels = rawTaskStatuses.map((rawTaskStatus) => {
  return {
    value: rawTaskStatus.value,
    label: rawTaskStatus.shortLabel
  }
});

// Date-only (YYYY-MM-DD) and ISO timestamp strings sort correctly as plain strings; blanks always last when ascending.
const compareDateStrings = (rowA, rowB, columnId) => {
  const a = rowA.values[columnId] || "";
  const b = rowB.values[columnId] || "";
  if (a === b) return 0;
  if (a === "") return 1;
  if (b === "") return -1;
  return a < b ? -1 : 1;
};

// Default order for My Tasks and the Tasks page: starred first (in the user's chosen ordering), then Next Action
// ascending with blank dates last, matching the Next Action column header sort. Ties keep the server's order.
const compareTasksStarredOrderingDue = (a, b) => {
  return (b.starred ? 1 : 0) - (a.starred ? 1 : 0) ||
    (a.starred ? (a.ordering ?? 0) - (b.ordering ?? 0) : 0) ||
    compareDateStrings({ values: { due: a.due } }, { values: { due: b.due } }, "due");
};

// Same comparison for table rows shaped {task}
const compareStarredOrderingDue = (rowA, rowB) => compareTasksStarredOrderingDue(rowA.task, rowB.task);

const STATUS_RANKS = { CREATED: 1, IN_PROGRESS: 2, BLOCKED: 3, BLOCKED_INTERNALLY: 4, BLOCKED_INTERNALLY_PROVIDER: 5, BLOCKED_SUBMITTED: 6, COMPLETE: 7 };

const StarCell = ({ value, row, reloadTaskLists, canStar }) => {

  const [updateStarProcessing, setUpdateStarProcessing] = useState(false);

  const { isH3Admin, authorities } = useContext(UserSettingsContext);
  const canEdit = isH3Admin || authorities.includes("TASK_EDIT");

  const notyf = useContext(NotyfContext);

  const [localStarred, setLocalStarred] = useState(row.original.task.starred);

    // keep local state in sync when the row updates from the server
  useEffect(() => {
    setLocalStarred(row.original.task.starred);
  }, [row.original.task.id, row.original.task.starred]);
  
  const handleStarClick = (e, id) => {
    e.preventDefault();

    if (!canEdit || !canStar)
    {
      return;
    }

    // Update the row value locally
    const prev = localStarred;
    setLocalStarred(!prev);

    setUpdateStarProcessing(true);
    authenticatedFetch({
      path: "task/star/" + id,
      additionalHeaders: {"taskId": id},
      method: "PUT",
      successHandler: function(result) {
        reloadTaskLists();
        setUpdateStarProcessing(false);
      },
      errorHandler: function(error) {
        setUpdateStarProcessing(false);
        setLocalStarred(prev);
        reloadTaskLists(); // re-render the star
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }

  return (
    <>
      {updateStarProcessing ? <Spinner className="d-block mx-auto" animation="border" size="sm" variant="primary"/> : (
          localStarred ? <Star className="d-block mx-auto" onClick={(e) => handleStarClick(e, row.original.task.id)} style={{height: '16px', width: '16px', cursor: (canStar && canEdit) ? "pointer": "default", color: '#E5A54B', fill: "#E5A54B"}} /> : <Star className="d-block mx-auto" onClick={(e) => handleStarClick(e, row.original.task.id)} style={{height: '16px', width: '16px', cursor: (canStar && canEdit) ? "pointer": "default"}} />
      )}
    </>
  )
};

// Cross-team lists (Revenue Teams page) must switch the active revenue team before navigating;
// single-team lists (Tasks page) just navigate.
const useTaskNavigation = (switchTeam) => {
  const notyf = useContext(NotyfContext);
  const navigate = useNavigate();

  return (e, revenueTeamId, path) => {
    e.stopPropagation();
    e.preventDefault();

    if (!switchTeam) {
      if (path) {
        window.location = path;
      }
      return;
    }

    if (revenueTeamId === null || revenueTeamId === 0 || (path !== "/" && !path)) {
      return;
    }

    authenticatedFetch({
      path: "auth/revenueteam",
      method: "PUT",
      requestData: {revenueTeamId: revenueTeamId},
      successHandler: function() {
          sessionStorage.setItem("revenueTeamId", revenueTeamId);
          navigate(path);
          window.scrollTo(0, 0);
      },
      errorHandler: function() {
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  };
};

const PracticeCell = ({ value, row, switchTeam }) => {
  const go = useTaskNavigation(switchTeam);
  const task = row.original.task;
  if (!task.practiceId) {
    return <>{value}</>;
  }
  return <a href="#" onClick={(e) => go(e, task.revenueTeamId, "/practice/" + task.practiceId)}>{value}</a>;
};

const ProviderCell = ({ value, row, switchTeam }) => {
  const go = useTaskNavigation(switchTeam);
  const task = row.original.task;
  if (task.providerIsDeleted || !task.providerId) {
    return <>{value}</>;
  }
  return <a href="#" onClick={(e) => go(e, task.revenueTeamId, "/provider/" + task.providerId)}>{value}</a>;
};

// Plain-text cells use one smaller size so they sit below the provider name in the visual hierarchy.
const CELL_TEXT_STYLE = { fontSize: "0.9em" };

// Compact icon buttons for the Actions column
const ACTION_BUTTON_STYLE = { width: "24px", height: "24px", padding: 0, lineHeight: 1, display: "inline-flex", alignItems: "center", justifyContent: "center" };
const ACTION_ICON_STYLE = { width: "13px", height: "13px" };

// Next action date, colored red when overdue and yellow when due within 4 days
const DueCell = ({ value, row }) => (
  <EditableExpires localExpires={value} localActive={true} editingExpires={false} redDays={-1} yellowDays={4} warnWithColors={row.original.task.status !== "COMPLETE"} />
);

const LastActivityCell = ({ value }) => {
  const display = getRelativeTimeDisplay(value);
  if (!display) {
    return null;
  }

  return <span title={display.title} style={CELL_TEXT_STYLE}>{display.text}</span>;
};

const ContextChip = ({ label, value }) => (
  <OverlayTrigger placement="top" overlay={<Tooltip>{label}</Tooltip>}>
    <Badge bg="" className="me-1 mb-1 text-wrap text-start" style={{ backgroundColor: "#EEF0F0", color: "#3B4244", fontWeight: 500 }}>{value}</Badge>
  </OverlayTrigger>
);

const ContextCell = ({ row }) => {
  const task = row.original.task;
  return (
    <div style={{ minWidth: "140px" }}>
      {task.carrierName ? <ContextChip label="Carrier" value={task.carrierName}/> : null}
      {task.hubName ? <ContextChip label="Data Hub" value={task.hubName}/> : null}
      {task.facilityName ? <ContextChip label="Facility" value={task.facilityName}/> : null}
    </div>
  );
};

// Provider with the revenue team (cross-team lists only) as a quiet tag beneath it.
const ProviderRevenueTeamCell = ({ value, row, switchTeam }) => {
  const task = row.original.task;
  return (
    <div style={{ whiteSpace: "nowrap", lineHeight: 1.3 }}>
      {value ? <div style={CELL_TEXT_STYLE}><ProviderCell value={value} row={row} switchTeam={switchTeam}/></div> : null}
      {switchTeam && task.revenueTeamName ? (
        <Badge bg="" title="Revenue team" style={{ backgroundColor: "#F1F3F3", color: "#6B7375", fontWeight: 500, fontSize: "0.72em", padding: "0.3em 0.4em", marginLeft: "-0.3em", marginTop: value ? "3px" : 0 }}>
          {task.revenueTeamName}
        </Badge>
      ) : null}
    </div>
  );
};

const getInitials = (assignee) => ((assignee.firstName || "").charAt(0) + (assignee.lastName || "").charAt(0)).toUpperCase();

const AssigneesCell = ({ value }) => {
  if (!value || value.length === 0) {
    return null;
  }

  return (
    <span style={{ display: "inline-flex", flexWrap: "wrap", gap: "2px" }}>
      {value.map((assignee, i) => {
        const name = (assignee.firstName || "") + " " + (assignee.lastName || "");
        return (
          <OverlayTrigger key={i} placement="top" overlay={<Tooltip>{name}</Tooltip>}>
            {assignee.photoUrl ?
              <img src={assignee.photoUrl} width="24" height="24" className="rounded-circle" alt={name}/>
              :
              <span className="rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: "24px", height: "24px", fontSize: "0.65em", fontWeight: 600, backgroundColor: "#D9E5E2", color: "#2F4F4A" }}>{getInitials(assignee)}</span>
            }
          </OverlayTrigger>
        );
      })}
    </span>
  );
};

const AdditionalActionsCell = ({ value, row, reloadTaskLists, restrictActions }) => {
  const { isH3Admin, authorities } = useContext(UserSettingsContext);
  const canEdit = !restrictActions || isH3Admin || authorities.includes("TASK_EDIT");
  const isComplete = restrictActions && row.original.task.status === "COMPLETE";


  const [showAdditionalActionsModal, setShowAdditionalActionsModal] = useState(false);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);

  const handleShowAdditionalActions = (e) => { 
    setShowAdditionalActionsModal(true);
  }

  const handleAdditionalActionsModalClose = () => {
      setShowAdditionalActionsModal(false);
  }

  const handleShowInstructions = (e) => {
    setShowInstructionsModal(true);
  }

  const handleInstructionsModalClose = () => {
      setShowInstructionsModal(false);
  }

  const availableCarrierSubmissionActionTypeDisplay = getAvailableCarrierSubmissionActionTypeDisplay(row.original.task.availableCarrierSubmissionActionType);
  const availableCarrierSubmissionActionTypeErrorsDisplay = row.original.task.availableCarrierSubmissionActionTypeErrors ? row.original.task.availableCarrierSubmissionActionTypeErrors.join("; ") : "";

  const hasAdditionalInstructions = row.original.task.additionalInstructionsEntityName && row.original.task.additionalInstructionsEntityName !== "";

  return (
    <span style={{whiteSpace: "nowrap"}}>
      { canEdit && hasAdditionalInstructions ?
        <>&nbsp;<OverlayTrigger placement="bottom" overlay={<Tooltip>View instructions</Tooltip>}><Button onClick={(e) => handleShowInstructions(e)} variant="primary" size="sm" style={ACTION_BUTTON_STYLE} aria-label="View instructions"><FontAwesomeIcon icon={faInfoCircle} style={ACTION_ICON_STYLE}/></Button></OverlayTrigger></>
        : <></>
      }
      { canEdit && !isComplete && availableCarrierSubmissionActionTypeDisplay !== "" ?
        <>&nbsp;<OverlayTrigger placement="bottom" overlay={<Tooltip>Run automated action</Tooltip>}><Button onClick={(e) => handleShowAdditionalActions(e)} variant="primary" size="sm" style={ACTION_BUTTON_STYLE} aria-label="Run automated action"><FontAwesomeIcon icon={faRobot} style={ACTION_ICON_STYLE}/></Button></OverlayTrigger></>
        : 
        (availableCarrierSubmissionActionTypeErrorsDisplay && !isComplete ? 
          <OverlayTrigger
            triggers="[hover,focus]"
            placement="bottom"
            overlay={<Tooltip>{availableCarrierSubmissionActionTypeErrorsDisplay}</Tooltip>}
            >
              <span>&nbsp;<Button variant="warning" size="sm" style={ACTION_BUTTON_STYLE}><FontAwesomeIcon icon={faRobot} style={ACTION_ICON_STYLE}/></Button></span>
          </OverlayTrigger>
        : <></>
        )
      }
      <TaskAdditionalActionsModal show={showAdditionalActionsModal} onClose={handleAdditionalActionsModalClose} id={row.original.task.id} availableCarrierSubmissionActionType={row.original.task.availableCarrierSubmissionActionType} reloadTaskLists={reloadTaskLists} />
      <TaskAdditionalInstructionsModal show={showInstructionsModal} onClose={handleInstructionsModalClose} id={row.original.task.id} category={row.original.task.category} entityName={row.original.task.additionalInstructionsEntityName} practiceName={row.original.task.practiceName} />
    </span> 
  )
};


// Multi-select filter shown in the toolbar above the table (not in the header cell).
// filterValue is an array of {value, label}, which the custom filter functions below match against.
// Shared height so the toolbar's filter buttons, view toggle and assignee select line up.
const TOOLBAR_CONTROL_HEIGHT = 30;
const TOOLBAR_BUTTON_STYLE = { height: TOOLBAR_CONTROL_HEIGHT, padding: "0 0.6rem", lineHeight: 1, display: "inline-flex", alignItems: "center" };

// React 17 has no useId; My Tasks and Team Tasks both mount a dropdown with the same label, so each instance needs its own prefix.
let nextTaskFilterId = 0;

const TaskFilterDropdown = ({ label, options, filterValue, setFilter }) => {
  const [idPrefix] = useState(() => "task-filter-" + nextTaskFilterId++);
  const selected = filterValue || [];
  const isSelected = (option) => selected.some((s) => s.value === option.value);

  const toggle = (option) => {
    const next = isSelected(option) ? selected.filter((s) => s.value !== option.value) : [...selected, option];
    setFilter(next.length === 0 ? undefined : next);
  };

  return (
    <Dropdown autoClose="outside">
      <Dropdown.Toggle variant={selected.length > 0 ? "primary" : "outline-secondary"} size="sm" style={TOOLBAR_BUTTON_STYLE}>
        {label}
        {selected.length > 0 && <Badge bg="light" text="dark" pill className="ms-2">{selected.length}</Badge>}
      </Dropdown.Toggle>
      <Dropdown.Menu style={{ minWidth: "220px", maxHeight: "320px", overflowY: "auto", fontSize: "0.875rem" }}>
        {options.map((option) => (
          <Form.Check
            key={option.value}
            type="checkbox"
            id={idPrefix + "-" + option.value}
            className="px-4 py-1"
            label={option.label}
            checked={isSelected(option)}
            onChange={() => toggle(option)}
          />
        ))}
        {selected.length > 0 && (
          <>
            <Dropdown.Divider />
            <Dropdown.Item as="button" onClick={() => setFilter(undefined)}>Clear {label.toLowerCase()}</Dropdown.Item>
          </>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

function customStatusFilter(rows, columnIds, filterValues) {
  return rows.filter((row) => {
    for (var i = 0; i < filterValues.length; i++) {
      if (filterValues[i].value === row.original.task.status) {
        return true;
      }
    }
    
    return false;
  });
}

function customCategoryFilter(rows, columnIds, filterValues) {
  return rows.filter((row) => {
    for (var i = 0; i < filterValues.length; i++) {
      if (filterValues[i].value === row.original.task.category.value) {
        return true;
      }
    }
    
    return false;
  });
}


// options:
//   reloadTaskLists  - refresh callback passed to cells that modify a task
//   switchTeam       - links must switch the active revenue team first (cross-team lists)
//   canStar          - the star can be toggled from this list
//   showStar         - include the star column
//   reorderable      - widen the star column to make room for the drag handle
//   includeComplete  - the status filter offers COMPLETE
//   restrictActions  - hide automation / instruction buttons from non-editors and on completed tasks
const buildTaskColumns = ({ reloadTaskLists, switchTeam = false, canStar = true, showStar = true, reorderable = false, includeComplete = false, restrictActions = false }) => {
  const starWidth = reorderable ? STAR_COLUMN_WIDTH_WITH_HANDLE : STAR_COLUMN_WIDTH;

  const columns = [
    ...(showStar ? [
    {
      Header: "",
      accessor: "task.starred",
      // Starred first, then the user's ordering among starred tasks. Ties fall through to the next sort (Next Action).
      sortType: (rowA, rowB) => {
        const a = rowA.original.task;
        const b = rowB.original.task;
        return (b.starred ? 1 : 0) - (a.starred ? 1 : 0) || (a.starred ? (a.ordering ?? 0) - (b.ordering ?? 0) : 0);
      },
      key: "starred",
      starWidth,
      sticky: { left: 0 },
      Cell: ({ value, row }) => (
        <StarCell value={value} row={row} reloadTaskLists={reloadTaskLists} canStar={canStar}/>
      )
    }
    ] : []),
    {
      Header: "Provider",
      accessor: "task.providerLastNameFirstName",
      key: "providerName",
      sticky: { left: showStar ? starWidth : 0 },
      Cell: ({ value, row }) => (
        <ProviderRevenueTeamCell value={value} row={row} switchTeam={switchTeam}/>
      )
    },
    {
      Header: "Practice",
      accessor: "task.practiceName",
      key: "practiceName",
      Cell: ({ value, row }) => (
        <span style={{ whiteSpace: "nowrap", ...CELL_TEXT_STYLE }}><PracticeCell value={value} row={row} switchTeam={switchTeam}/></span>
      )
    },
    {
      Header: "Status",
      accessor: "task.status",
      key: "status",
      filterLabel: "Status",
      filterOptions: rawTaskStatusesWithShortLabels.filter((status) => status.value !== "" && (includeComplete || status.value !== "COMPLETE")),
      filter: customStatusFilter,
      sortType: (rowA, rowB, columnId) => (STATUS_RANKS[rowA.values[columnId]] || 1) - (STATUS_RANKS[rowB.values[columnId]] || 1),
      Cell: ({ value }) => <TaskStatusBadge status={value} showDot/>,
    },
    {
      Header: "Next Action",
      accessor: "task.due",
      key: "due",
      sortType: compareDateStrings,
      Cell: ({ value, row }) => (
        <DueCell value={value} row={row}/>
      )
    },
  ];

  columns.push(
    {
      Header: "Category",
      accessor: "task.category.label",
      key: "category",
      filterLabel: "Category",
      filterOptions: rawTaskCategories.filter((category) => category.value !== ""),
      filter: customCategoryFilter,
      Cell: ({ value }) => (
          <Badge pill className="float-start" bg="secondary">
            {value}
          </Badge>
      )
    },
    {
      Header: "External Entity",
      accessor: item => [item.task.carrierName, item.task.hubName, item.task.facilityName].filter(Boolean).join(" "),
      key: "context",
      Cell: ({ row }) => (
        <ContextCell row={row}/>
      )
    },
    {
      Header: "Summary",
      accessor: "task.other",
      key: "other",
      Cell: ({ value }) => <div style={{ ...CELL_TEXT_STYLE, minWidth: "150px" }}>{value}</div>
    },
    {
      Header: "Actions",
      key: "otherActions",
      Cell: ({ value, row }) => (
        <AdditionalActionsCell value={value} row={row} reloadTaskLists={reloadTaskLists} restrictActions={restrictActions}/>
      )
    },
    {
      Header: "Last Activity",
      accessor: item => item.task.activity.length > 0 ? item.task.activity[0].created : null,
      key: "lastActivityDate",
      sortType: compareDateStrings,
      Cell: ({ value }) => <LastActivityCell value={value}/>
    }
  );

  columns.push({
    Header: () => <span title="Assignees" aria-label="Assignees"><FontAwesomeIcon icon={faUser}/></span>,
    accessor: "task.assignees",
    key: "assignees",
    Cell: ({ value }) => <AssigneesCell value={value}/>
  });

  return columns;
};

const HEADER_STYLE = { fontSize: "0.78rem", fontWeight: 600, color: "#6B7375", verticalAlign: "middle" };
const SORT_ICON_STYLE = { fontSize: "0.85em" };

const NO_SORT_HEADER_KEYS = ["assignees"];

// children render above the table (e.g. view toggles)
// reorder: optional {onReorder(movedTaskId, targetTaskId), savingId (id of the task whose new order is being saved)}. Enables dragging starred rows (by their handle)
// onto another starred row, which moves the dragged task above it. Only usable while no column sort is active.
const TaskTable = ({ columns, data, initialState, getRowId, isLoaded, error, reloadTaskLists, taskToOpen, reorder, children }) => {
  const { getTableProps, getTableBodyProps, headerGroups, allColumns, setAllFilters, state, rows, prepareRow } = useTable(
    {
      columns,
      data,
      getRowId,
      initialState,
      autoResetSortBy: false,
      autoResetExpanded: false,
      autoResetFilters: false,
    },
    useFilters,
    useSortBy,
    useExpanded,
  );

  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const dragIdRef = useRef(null); // read in the drop handler so it doesn't depend on render timing
  const onReorderRef = useRef(null);
  onReorderRef.current = reorder ? reorder.onReorder : null;

  // Stable callbacks so memoized rows don't re-render on every drag event
  const handleRowDragStart = useCallback((id) => { dragIdRef.current = id; setDragId(id); }, []);
  const handleRowDragOver = useCallback((id) => setOverId(id), []);
  const handleRowDragEnd = useCallback(() => { dragIdRef.current = null; setDragId(null); setOverId(null); }, []);
  const handleRowDrop = useCallback((targetId) => {
    const movedId = dragIdRef.current;
    dragIdRef.current = null;
    setDragId(null);
    setOverId(null);
    if (movedId !== null && movedId !== targetId && onReorderRef.current) {
      onReorderRef.current(movedId, targetId);
    }
  }, []);

  const reorderAvailable = !!reorder;
  // Reordering only makes sense in the default order: star column ascending, optionally then Next Action ascending
  const reorderSortActive = reorderAvailable && state.sortBy.length > 0 && state.sortBy.length <= 2 &&
    state.sortBy[0].id === "task.starred" && !state.sortBy[0].desc &&
    (state.sortBy.length === 1 || (state.sortBy[1].id === "task.due" && !state.sortBy[1].desc));
  // While a reorder is being saved, further drags are disabled so saves can't overlap
  const reorderSavingId = reorder ? reorder.savingId : null;
  const reorderEnabled = reorderSortActive && reorderSavingId == null;
  const reorderHint = reorderEnabled ? null : (reorderSavingId != null ? "Saving..." : "Sort by Starred to enable drag-n-drop");

  const colSpan = columns.length;
  const filterColumns = allColumns.filter((column) => column.filterOptions);
  const hasActiveFilters = state.filters.length > 0;

  return (
    <Card>
      <Card.Body style={{overflow: "auto", minHeight: "400px", maxHeight: "calc(100vh - 220px)", padding: 0}}>
        <div className="d-flex flex-wrap align-items-center mb-3 pt-3 px-3" style={{ gap: "8px", position: "relative", zIndex: 4 }}>
          {children}
          {filterColumns.map((column) => (
            <TaskFilterDropdown key={column.id} label={column.filterLabel} options={column.filterOptions} filterValue={column.filterValue} setFilter={column.setFilter}/>
          ))}
          {hasActiveFilters && <Button variant="link" size="sm" onClick={() => setAllFilters([])}>Clear filters</Button>}
        </div>
        <Table className="table-sm" striped bordered hover {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => {
                  const { key, ...restHeaderProps } = NO_SORT_HEADER_KEYS.includes(column.key) ? column.getHeaderProps() : column.getHeaderProps(column.getSortByToggleProps());
                  return (
                    <th {...restHeaderProps} key={key} style={{...restHeaderProps.style, ...HEADER_STYLE, ...getStickyStyle(column, true)}}>
                      {column.render("Header")}
                      {(!NO_SORT_HEADER_KEYS.includes(column.key) && column.key !== "otherActions") ? (
                        column.isSorted ? (
                          column.isSortedDesc ? (
                            <FontAwesomeIcon icon={faSortDown} className="ms-2" style={SORT_ICON_STYLE} />
                          ) : (
                            <FontAwesomeIcon icon={faSortUp} className="ms-2" style={SORT_ICON_STYLE} />
                          )
                        ) : (
                          <FontAwesomeIcon icon={faSort} className="ms-2" style={{ ...SORT_ICON_STYLE, opacity: 0.35 }} />
                        )
                      ) : null}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {
            error ? (<tr><td colSpan={colSpan}>Something went wrong; please reload the page...</td></tr>) : (
              !isLoaded ? (<tr><td colSpan={colSpan}>Loading...</td></tr>) : (
              rows.map((row) => {
                prepareRow(row);
                const { key } = row.getRowProps();
                return (
                  <TaskListRow showModal={taskToOpen && taskToOpen === row.original.task.id} key={key} row={row} reloadTaskLists={reloadTaskLists}
                    reorderAvailable={reorderAvailable} reorderEnabled={reorderEnabled} reorderHint={reorderHint} dragInProgress={dragId !== null}
                    isDragging={dragId === row.original.task.id} isDropTarget={dragId !== null && overId === row.original.task.id && dragId !== row.original.task.id}
                    onRowDragStart={handleRowDragStart} onRowDragOver={handleRowDragOver} onRowDrop={handleRowDrop} onRowDragEnd={handleRowDragEnd}/>
                );
              })))
            }
            {(!error && isLoaded) &&
            <tr>
              <td colSpan={colSpan}>Total: <b>{rows.length}</b></td>
            </tr>
            }
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

export { buildTaskColumns, compareStarredOrderingDue, compareTasksStarredOrderingDue, rawTaskStatusesWithShortLabels, TOOLBAR_CONTROL_HEIGHT, TOOLBAR_BUTTON_STYLE };
export default TaskTable;
